import { useState, useEffect, useCallback } from "react";
import authApi from "../api/authApi";

const CACHE_KEY = "total_user_cache";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Global promise to prevent multiple simultaneous requests
let fetchPromise = null;

// Utility function to get cached totalUser
const getCachedTotalUser = () => {
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached) return null;

        const { value, timestamp } = JSON.parse(cached);
        const now = Date.now();

        // Check if cache is still valid (within 24 hours)
        if (now - timestamp < CACHE_DURATION) {
            return value;
        }

        // Cache expired, remove it
        localStorage.removeItem(CACHE_KEY);
        return null;
    } catch (error) {
        console.error("Error reading totalUser cache:", error);
        return null;
    }
};

// Utility function to set cached totalUser
const setCachedTotalUser = (value) => {
    try {
        const cacheData = {
            value,
            timestamp: Date.now(),
        };
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
        console.error("Error setting totalUser cache:", error);
    }
};

// Custom hook to fetch and cache totalUser
const useTotalUser = (shouldFetch = true) => {
    const [totalUser, setTotalUser] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const fetchTotalUser = useCallback(async (forceRefresh = false) => {
        // Check cache first if not forcing refresh
        if (!forceRefresh) {
            const cached = getCachedTotalUser();
            if (cached !== null) {
                setTotalUser(cached);
                return cached;
            }
        }

        // If there's already a fetch in progress, wait for it
        if (fetchPromise) {
            try {
                const result = await fetchPromise;
                setTotalUser(result);
                return result;
            } catch (error) {
                // If the shared promise fails, try cache as fallback
                const cached = getCachedTotalUser();
                if (cached !== null) {
                    setTotalUser(cached);
                    return cached;
                }
                return 0;
            }
        }

        // Fetch from API
        setIsLoading(true);
        fetchPromise = (async () => {
            try {
                const { data } = await authApi.totalUser();
                const userCount = data.totalUser || 0;
                setCachedTotalUser(userCount);
                return userCount;
            } catch (error) {
                console.error("Error fetching totalUser:", error);
                // If error, try to use cached value as fallback
                const cached = getCachedTotalUser();
                if (cached !== null) {
                    return cached;
                }
                throw error;
            } finally {
                fetchPromise = null;
            }
        })();

        try {
            const result = await fetchPromise;
            setTotalUser(result);
            return result;
        } catch (error) {
            const cached = getCachedTotalUser();
            if (cached !== null) {
                setTotalUser(cached);
                return cached;
            }
            return 0;
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (shouldFetch) {
            fetchTotalUser();
        }
    }, [shouldFetch, fetchTotalUser]);

    return { totalUser, isLoading, fetchTotalUser, refreshTotalUser: () => fetchTotalUser(true) };
};

export default useTotalUser;
export { getCachedTotalUser, setCachedTotalUser };
